/**
 * The team the app is currently working in.
 *
 * A tenant can hold several teams, but until this existed nothing above a
 * single page knew which one you were in: the `/team` page kept its selection in
 * local component state, and every collaborative surface built its own
 * `TeamRef` out of the TENANT id — the backend's "primary team" alias. So the
 * assignee picker and the @-mention roster in the transaction workspace always
 * listed the tenant's DEFAULT team, and in a multi-team tenant you could not
 * mention or assign the colleague sitting next to you on the team you were
 * actually working in.
 *
 * The selection is persisted per identity (`createScopedStorage` keys on
 * app/customer/org/tenant/user), so it survives a reload and cannot bleed across
 * a tenant switch. It is deliberately NOT in the server-backed prefs blob: that
 * row is a cross-device preference, and "which team am I in" is closer to where
 * you are than to how you like things.
 *
 * `team` is never null. Until the list arrives — or for a caller who belongs to
 * no team yet — it is the tenant's primary-team alias, which
 * `teams-mgmt-api`'s `resolve_team_id` maps onto the caller's own default team
 * (and lazily provisions). That is what keeps every consumer a plain
 * `useActiveTeam().team` with no loading branch of its own.
 *
 * Two things the alias must NOT do, both of which it used to:
 *
 *  - carry the TENANT's name. The alias's id is the tenant id because the
 *    backend resolves it, but presenting it as a team called "Acme Prod" told
 *    the operator their tenant was a team — on a product where a tenant holds
 *    several teams and sharing is scoped to one, that is a lie about audience.
 *  - stand in silently for a FAILED read. `useMyTeams().error` was dropped
 *    here, so a failed team list resolved to the alias and the `/team` page
 *    then hid its picker, leaving "you have one team, and it is your tenant".
 *    The failure is now carried on the context and rendered.
 */
import * as React from "react";
import type { TeamRef } from "@authe/fe-commons/teams";
import { createScopedStorage } from "@authe/fe-commons/storage";
import { useSession } from "@authe/ui-core/session";
import { useMyTeams } from "@/features/team/useMyTeams";
import { useStorageIdentity } from "./storageIdentity";

/** Storage key for the selected team id, inside the identity-scoped keyspace. */
const ACTIVE_TEAM_KEY = "active-team-id";

export interface ActiveTeam {
  /** The caller's teams in the active tenant, names hydrated. */
  teams: TeamRef[];
  /** The team to scope team-shaped reads to. Never null — see the module note. */
  team: TeamRef;
  /** The explicitly selected id, or `null` while the primary-team fallback stands. */
  selectedTeamId: string | null;
  /** Switch the active team. Ignored for an id the caller does not belong to. */
  setActiveTeamId: (teamId: string) => void;
  /** The team list is still loading. */
  isLoading: boolean;
  /**
   * The team list failed to load, so `teams` is not an answer and `team` is the
   * backend's default-team alias rather than a team the caller chose.
   */
  failed: boolean;
  /** The failure itself, for a typed error surface. */
  error: unknown;
  /**
   * True while `team` is the primary-team ALIAS rather than one of `teams`.
   * A surface that names the team should say "your default team", not a name.
   */
  isFallback: boolean;
  /** Re-read the team list. */
  refetch: () => void;
  /** Refetch the team list (after creating one). */
  invalidate: () => void;
}

const ActiveTeamContext = React.createContext<ActiveTeam | null>(null);

/**
 * The tenant's primary-team alias — what stands in until a real team is known.
 *
 * Its id is the tenant id, which `teams-mgmt-api`'s `resolve_team_id` maps onto
 * the caller's own default team. Its NAME is deliberately generic: it took the
 * tenant's display name, so every surface that renders `team.name` announced
 * the tenant as a team. The two are different scopes — a tenant holds teams,
 * and a share scoped to "my team" is not a share with the tenant — so naming
 * one after the other misdescribes who can see what.
 */
export function primaryTeamAlias(tenant: {
  orgId: string | null;
  tenantId: string | null;
}): TeamRef {
  return {
    id: tenant.tenantId ?? "default",
    name: "Your default team",
    tenant: { orgId: tenant.orgId, tenantId: tenant.tenantId },
  };
}

/**
 * Which team is effective, given the caller's teams and their stored pick.
 *
 * Pure so it can be tested without a provider (these packages render hooks with
 * `renderToStaticMarkup`, which runs no effects). Three rules:
 *
 * - a stored id the caller still holds wins;
 * - a stored id they no longer hold — removed from the team, or left over from
 *   a tenant they switched away from — falls back to their FIRST team, never to
 *   the alias, so the picker never sits on a value none of its options match;
 * - no teams at all resolves to the primary-team alias, which the backend maps
 *   onto the caller's own default team.
 */
export function resolveActiveTeam(input: {
  teams: TeamRef[];
  selectedTeamId: string | null;
  tenant: { orgId: string | null; tenantId: string | null };
}): { team: TeamRef; selectedTeamId: string | null; isFallback: boolean } {
  const { teams, selectedTeamId, tenant } = input;
  const held = teams.find((t) => t.id === selectedTeamId);
  const effective = held ?? teams[0] ?? null;
  return {
    team: effective ?? primaryTeamAlias(tenant),
    selectedTeamId: effective?.id ?? null,
    isFallback: effective === null,
  };
}

export function ActiveTeamProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { session } = useSession();
  const identity = useStorageIdentity();
  const { teams, isLoading, error, refetch, invalidate } = useMyTeams();

  const storage = React.useMemo(
    () => createScopedStorage(identity),
    // The identity object is rebuilt each render; its FIELDS are the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identity.app, identity.orgId, identity.tenantId, identity.userId],
  );

  const [selectedTeamId, setSelectedTeamId] = React.useState<string | null>(() =>
    storage.get<string | null>(ACTIVE_TEAM_KEY, null),
  );

  // A tenant switch re-keys the storage, so re-seed from the new keyspace
  // rather than carrying the previous tenant's team id into it.
  const seededFrom = React.useRef(storage);
  if (seededFrom.current !== storage) {
    seededFrom.current = storage;
    setSelectedTeamId(storage.get<string | null>(ACTIVE_TEAM_KEY, null));
  }

  const setActiveTeamId = React.useCallback(
    (teamId: string) => {
      setSelectedTeamId(teamId);
      storage.set(ACTIVE_TEAM_KEY, teamId);
    },
    [storage],
  );

  const resolved = React.useMemo(
    () =>
      resolveActiveTeam({
        teams,
        selectedTeamId,
        tenant: {
          orgId: session?.tenant.orgId ?? null,
          tenantId: session?.tenant.tenantId ?? null,
        },
      }),
    [teams, selectedTeamId, session],
  );

  const value = React.useMemo<ActiveTeam>(
    () => ({
      teams,
      team: resolved.team,
      selectedTeamId: resolved.selectedTeamId,
      setActiveTeamId,
      isLoading,
      // Carried, not swallowed: the alias is a reasonable thing to CALL with
      // and a terrible thing to present as the caller's team.
      failed: error != null,
      error,
      isFallback: resolved.isFallback,
      refetch,
      invalidate,
    }),
    [teams, resolved, setActiveTeamId, isLoading, error, refetch, invalidate],
  );

  return <ActiveTeamContext.Provider value={value}>{children}</ActiveTeamContext.Provider>;
}

/**
 * The active team. Outside the provider this falls back to a global/empty ref
 * rather than throwing, so a component rendered by a test or a signed-out shell
 * still gets a usable `TeamRef` — the same tolerance `usePermissions` has.
 */
export function useActiveTeam(): ActiveTeam {
  const value = React.useContext(ActiveTeamContext);
  return value ?? FALLBACK;
}

const FALLBACK: ActiveTeam = {
  teams: [],
  team: { id: "default", name: "Your default team", tenant: { orgId: null, tenantId: null } },
  selectedTeamId: null,
  setActiveTeamId: () => {},
  isLoading: false,
  failed: false,
  error: null,
  isFallback: true,
  refetch: () => {},
  invalidate: () => {},
};
